import { DiscoveryProductsRecentlyAddedResponseType } from "@sellar-io/api-v2/types";

import { useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { SpinnerLegacy } from "../../../../components";
import { BestPricePromiseDialog } from "../../../../components/bestPricePromise/BestPricePromiseDialog";
import { NavigationBar } from "../../../../components/navigation/NavigationBar";
import { DISCOVER } from "../../../../constants/routes";
import { useEvents } from "../../../../hooks";
import { useProductCuratedList } from "../../../../hooks/api/v2/product/useProductCuratedList";
import { getSellarWebsiteUtms } from "../../../../utils/utmHelper";
import { DiscoveryProductDialog } from "../components/DiscoveryProductDialog";
import {
  setRecentlyViewedExpiry,
  useDiscoveryV2RecentlyViewedStore,
} from "../DiscoveryV2RecentlyViewedStore";
import SpotlightFilterContainerCard from "./components/SpotlightFilterContainerCard";
import { SpotlightProductCard } from "./components/SpotlightProductCard";
import { useSpotlightFilterStore } from "./SpotlightFilterStore";

// Production IDs
const seasonalProducts = [
  20695, 29488, 2312, 71819, 44096, 351, 32344, 74085, 71972, 50504, 65512,
  45239, 6888, 33154, 36247, 75373, 62850, 24160, 41056, 62917, 61144, 53658,
  74068, 73026, 354, 353, 56878, 27822, 75385, 67235, 69443, 24203, 72737,
  72954, 72960, 44045, 47722, 50337, 63226, 62232, 76067, 10305, 76045, 76047,
  24642, 75242, 28944, 62264, 72203, 75924, 43308, 56895, 66975, 66967, 43521,
  70678, 71819, 76770, 45558, 47972, 76826, 56950, 77163, 75801, 76704,
];

export const LowAndNo = () => {
  const navigate = useNavigate();
  const { track } = useEvents();
  const [selectedProduct, setSelectedProduct] = useState<
    DiscoveryProductsRecentlyAddedResponseType[number] | undefined
  >(undefined);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);

  const { selectedContainerTypes } = useSpotlightFilterStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
    })),
  );

  const seasonalProductsQuery = useProductCuratedList(
    {
      query: [
        {
          type: "id",
          modifier: "IN",
          value: seasonalProducts,
        },
        ...(selectedContainerTypes.length
          ? ([
              {
                type: "containerType",
                modifier: "IN",
                value: selectedContainerTypes,
              },
            ] as const)
          : []),
      ],
      orderBy: "random",
      limit: 50,
    },
    {
      staleTime: 1000 * 60 * 10,
    },
  );

  const isLoading = seasonalProductsQuery.isLoading;

  const onProductClicked = (
    product: DiscoveryProductsRecentlyAddedResponseType[number],
  ) => {
    if (product.Supplier) {
      setSelectedProduct(product);
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: product.id,
        supplierId: product.Supplier.id,
        name: product.name ?? "",
        imageUrl: product.imageUrl,
        logoUrl: product.Supplier.Business?.logoUrl,
        urlId: product.Supplier.urlId,
        details: `${product.kind} // ${product.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: product.containsPreOrderProductItem,
      });

      track("Discover product clicked", {
        supplier: product.Supplier.urlId,
        product: product.name,
        source: "Recently added",
        containsPreOrderProductItem: product.containsPreOrderProductItem,
        ...getSellarWebsiteUtms(),
      });
    }
  };

  return (
    <>
      <div className="@container">
        <NavigationBar
          leftIcon={"CHEVRON_LEFT"}
          onClickLeftIcon={() => {
            navigate(DISCOVER);
          }}
          title={"Low & No on Sellar"}
        />
        <div className="w-full border-b border-zinc-300 bg-white p-4 py-6">
          <div className="m-auto flex w-full max-w-[1200px] flex-col-reverse items-center gap-4 @4xl:flex-row">
            <div className="flex w-full flex-col gap-2 @4xl:w-1/2">
              <img
                src="https://sellar.imgix.net/sellar-image-assets/lowandno/low-and-no-sticker.png"
                alt="Low and No"
                className="hidden w-60 flex-none @3xl:ml-0 @4xl:block"
              />
              <p>
                Stock your venue from the largest collection of low and no
                alcohol craft drinks in the UK. Offer your customers the
                freshest, most exciting drinks for their moderation exploration.
              </p>
            </div>
            <picture className="@4xl:h-[250px] @4xl:w-1/2">
              <source
                srcSet="https://sellar.imgix.net/sellar-image-assets/lowandno/low-and-no-banner-large.png"
                media="(min-width: 1400px)"
              />
              <source
                srcSet="https://sellar.imgix.net/sellar-image-assets/lowandno/low-and-no-banner-medium.png"
                media="(min-width: 968px)"
              />
              <img
                src="https://sellar.imgix.net/sellar-image-assets/lowandno/low-and-no-banner-small.png"
                alt="Low and no"
                className="h-full w-full self-center rounded-lg object-cover"
              />
            </picture>
          </div>
        </div>

        <div className="w-full px-4">
          <div className="m-auto grid w-full max-w-[1200px] flex-col gap-4 pt-4">
            <SpotlightFilterContainerCard />
            <div className={"pb-4"}>
              {isLoading ? (
                <SpinnerLegacy />
              ) : (
                <>
                  <div
                    className={twMerge(
                      "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
                    )}
                  >
                    {seasonalProductsQuery.data?.pages.map((page) =>
                      page.map((product) => (
                        <>
                          <SpotlightProductCard
                            key={product.id}
                            product={product}
                            onClick={() => onProductClicked(product)}
                          />
                        </>
                      )),
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {selectedProduct && (
        <>
          <DiscoveryProductDialog
            productId={selectedProduct.id}
            supplierId={selectedProduct.Supplier?.id}
            active={!!selectedProduct}
            onDismiss={() => {
              setSelectedProduct(undefined);
            }}
            setBestPricePromiseDialogActive={() => {
              setBestPricePromiseDialogActive(true);
            }}
          />

          <BestPricePromiseDialog
            productName={selectedProduct.name}
            buyerName={selectedProduct.name}
            supplierName={selectedProduct.Supplier?.Business?.name}
            active={bestPricePromiseDialogActive}
            onDismiss={() => setBestPricePromiseDialogActive(false)}
          />
        </>
      )}
    </>
  );
};
